<template>
  <div class="settings-page">
    <section class="settings-header">
      <div>
        <div class="settings-kicker">
          System Configuration
        </div>
        <h1>Settings</h1>
        <p>Manage operational values used across procurement, warehouse, finance, and administration modules.</p>
      </div>

      <div class="settings-header__meta">
        <div>
          <span>Total Settings</span>
          <strong>{{ settings.length }}</strong>
        </div>
        <div>
          <span>Status</span>
          <strong>{{ hasChanges ? 'Unsaved' : 'Saved' }}</strong>
        </div>
      </div>
    </section>

    <b-row>
      <b-col
        lg="3"
        class="mb-2"
      >
        <div class="settings-sidebar">
          <b-input-group class="input-group-merge settings-search">
            <b-input-group-prepend is-text>
              <feather-icon
                icon="SearchIcon"
                size="16"
              />
            </b-input-group-prepend>
            <b-form-input
              ref="settingsSearch"
              v-model="search"
              placeholder="Search settings"
              autocomplete="off"
              :readonly="false"
              @focus="unlockSettingsSearch"
              @mousedown="unlockSettingsSearch"
              @keydown.stop
            />
          </b-input-group>

          <button
            type="button"
            class="settings-category"
            :class="{ active: activeCategory === 'All' }"
            @click="activeCategory = 'All'"
          >
            <span>
              <feather-icon icon="LayersIcon" />
              All Settings
            </span>
            <b-badge pill>
              {{ settings.length }}
            </b-badge>
          </button>

          <button
            v-for="category in categorySummaries"
            :key="category.name"
            type="button"
            class="settings-category"
            :class="{ active: activeCategory === category.name }"
            @click="activeCategory = category.name"
          >
            <span>
              <feather-icon :icon="category.icon" />
              {{ category.name }}
            </span>
            <b-badge pill>
              {{ category.count }}
            </b-badge>
          </button>
        </div>
      </b-col>

      <b-col lg="9">
        <b-card
          no-body
          class="settings-workspace"
        >
          <div class="settings-toolbar">
            <div>
              <h2>{{ activeCategory }}</h2>
              <p>{{ filteredSettings.length }} configuration value{{ filteredSettings.length === 1 ? '' : 's' }}</p>
            </div>

            <div class="settings-actions">
              <b-button
                variant="outline-secondary"
                :disabled="!hasChanges || saving || loading"
                @click="resetChanges"
              >
                Reset
              </b-button>
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                :disabled="!hasChanges || saving || loading"
                @click="updateSetting"
              >
                <b-spinner
                  v-if="saving"
                  small
                  class="mr-50"
                />
                Save Changes
              </b-button>
            </div>
          </div>

          <div
            v-if="loading"
            class="settings-loading"
          >
            <b-spinner variant="primary" />
            <span>Loading settings</span>
          </div>

          <b-form
            v-else
            @submit.prevent="updateSetting"
            @keyup.enter.prevent=""
          >
            <section
              v-for="category in visibleCategories"
              :key="category.name"
              class="settings-section"
            >
              <div class="settings-section__header">
                <div class="settings-section__icon">
                  <feather-icon :icon="category.icon" />
                </div>
                <div>
                  <h3>{{ category.name }}</h3>
                  <p>{{ category.description }}</p>
                </div>
              </div>

              <b-row>
                <b-col
                  v-for="setting in category.settings"
                  :key="setting.setting_key"
                  cols="12"
                  xl="6"
                >
                  <div class="settings-field">
                    <div class="settings-field__label">
                      <label :for="setting.setting_key">
                        {{ title(setting.setting_key) }}
                      </label>
                    </div>

                    <b-form-textarea
                      v-if="isMultiline(setting)"
                      :id="setting.setting_key"
                      v-model="setting.setting_value"
                      rows="3"
                      max-rows="8"
                      required
                    />
                    <b-form-input
                      v-else
                      :id="setting.setting_key"
                      v-model="setting.setting_value"
                      :type="inputType(setting)"
                      required
                    />
                  </div>
                </b-col>
              </b-row>
            </section>

            <div
              v-if="!visibleCategories.length"
              class="settings-empty"
            >
              <feather-icon
                icon="SearchIcon"
                size="24"
              />
              <strong>No settings found</strong>
              <span>Try another keyword or category.</span>
            </div>
          </b-form>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BFormInput,
  BFormTextarea,
  BForm,
  BButton,
  BCard,
  BBadge,
  BInputGroup,
  BInputGroupPrepend,
  BSpinner,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from 'axios'
import apiEndpoints from '@/constants/apiEndpoints'

const categoryDefinitions = [
  {
    name: 'Organization',
    icon: 'BriefcaseIcon',
    description: 'Company structure, users, branches, regions, roles, and departments.',
    keywords: ['branch', 'region', 'department', 'departement', 'role', 'user', 'employee', 'company', 'organization'],
  },
  {
    name: 'Procurement',
    icon: 'ShoppingCartIcon',
    description: 'Purchase requests, purchase orders, suppliers, approvals, and receiving controls.',
    keywords: ['purchase', 'procurement', 'supplier', 'po', 'pr', 'mr', 'request', 'order', 'receive', 'approval'],
  },
  {
    name: 'Inventory',
    icon: 'PackageIcon',
    description: 'Items, stock, assets, warehouse, units, transfer, returns, and opname settings.',
    keywords: ['item', 'stock', 'asset', 'warehouse', 'inventory', 'unit', 'transfer', 'return', 'opname'],
  },
  {
    name: 'Finance',
    icon: 'DollarSignIcon',
    description: 'Invoice, payment, tax, currency, price, and finance integration settings.',
    keywords: ['invoice', 'payment', 'finance', 'tax', 'currency', 'price', 'cost', 'amount', 'budget'],
  },
  {
    name: 'Notifications',
    icon: 'BellIcon',
    description: 'Email, notification, message, and external communication settings.',
    keywords: ['email', 'mail', 'notification', 'notify', 'message', 'sms', 'whatsapp', 'alert'],
  },
  {
    name: 'Schedule',
    icon: 'ClockIcon',
    description: 'Dates, periods, working days, time zones, and recurring schedule settings.',
    keywords: ['date', 'time', 'period', 'schedule', 'day', 'month', 'year', 'timezone'],
  },
]

const systemCategory = {
  name: 'System',
  icon: 'SettingsIcon',
  description: 'Application defaults, integrations, numbering, and other platform-level settings.',
  keywords: [],
}

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BFormTextarea,
    BForm,
    BButton,
    BBadge,
    BInputGroup,
    BInputGroupPrepend,
    BSpinner,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      settings: [],
      loading: false,
      saving: false,
      search: '',
      activeCategory: 'All',
      originalSettingsSnapshot: '[]',
    }
  },
  computed: {
    hasChanges() {
      return JSON.stringify(this.settings) !== this.originalSettingsSnapshot
    },
    categorySummaries() {
      return this.categoriesWithSettings.map(category => ({
        name: category.name,
        icon: category.icon,
        count: category.settings.length,
      }))
    },
    filteredSettings() {
      const query = this.search.trim().toLowerCase()

      return this.settings.filter(setting => {
        const category = this.getCategory(setting.setting_key)
        const matchesCategory = this.activeCategory === 'All' || category.name === this.activeCategory
        const haystack = `${setting.setting_key} ${setting.setting_value || ''} ${this.title(setting.setting_key)}`.toLowerCase()
        const matchesSearch = !query || haystack.includes(query)

        return matchesCategory && matchesSearch
      })
    },
    visibleCategories() {
      return this.buildCategories(this.filteredSettings)
    },
    categoriesWithSettings() {
      return this.buildCategories(this.settings)
    },
  },
  mounted() {
    this.getSetting()
    this.$nextTick(this.unlockSettingsSearch)
  },
  methods: {
    unlockSettingsSearch() {
      const input = this.$refs.settingsSearch && this.$refs.settingsSearch.$el
      if (input) input.removeAttribute('readonly')
    },
    buildCategories(settings) {
      const categories = [...categoryDefinitions, systemCategory].map(category => ({
        ...category,
        settings: [],
      }))

      settings.forEach(setting => {
        const category = categories.find(item => item.name === this.getCategory(setting.setting_key).name)
        category.settings.push(setting)
      })

      return categories.filter(category => category.settings.length > 0)
    },
    getCategory(key) {
      const normalizedKey = String(key || '').toLowerCase()
      const matchedCategory = categoryDefinitions.find(category => (
        category.keywords.some(keyword => normalizedKey.includes(keyword))
      ))

      return matchedCategory || systemCategory
    },
    async getSetting() {
      this.loading = true

      try {
        const userToken = this.$cookies.get('userToken')
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        }
        const res = await axios.get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_SETTING}`, { headers })

        this.settings = res.data.data || []
        this.originalSettingsSnapshot = JSON.stringify(this.settings)
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Get Settings Failed',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
    async updateSetting() {
      if (!this.hasChanges || this.saving) return

      this.saving = true

      try {
        await axios.put(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_UPDATE_SETTING}`, { settings: this.settings })
        this.originalSettingsSnapshot = JSON.stringify(this.settings)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Save Settings Success',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Save Settings Failed',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.saving = false
      }
    },
    resetChanges() {
      this.settings = JSON.parse(this.originalSettingsSnapshot)
    },
    title(inputString) {
      return String(inputString || '')
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    isMultiline(setting) {
      const key = String(setting.setting_key || '').toLowerCase()
      const value = String(setting.setting_value || '')

      return value.length > 90 || ['template', 'description', 'message', 'notes', 'body'].some(keyword => key.includes(keyword))
    },
    inputType(setting) {
      const value = String(setting.setting_value || '')

      if (/^-?\d+(\.\d+)?$/.test(value)) return 'number'

      return 'text'
    },
  },
}
</script>

<style lang="scss" scoped>
.settings-page {
  .settings-header {
    display: flex;
    justify-content: space-between;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
    padding: 1.5rem;
    color: #fff;
    border-radius: 8px;
    background: linear-gradient(135deg, #1f2a44 0%, #255f85 52%, #2b8a7e 100%);
    box-shadow: 0 14px 34px rgba(31, 42, 68, 0.18);

    h1 {
      margin-bottom: 0.35rem;
      color: #fff;
      font-size: 1.75rem;
      font-weight: 700;
      letter-spacing: 0;
    }

    p {
      max-width: 680px;
      margin-bottom: 0;
      color: rgba(255, 255, 255, 0.78);
    }
  }

  .settings-kicker {
    margin-bottom: 0.25rem;
    color: rgba(255, 255, 255, 0.72);
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
  }

  .settings-header__meta {
    display: grid;
    grid-template-columns: repeat(2, minmax(110px, 1fr));
    gap: 0.75rem;
    min-width: 250px;

    div {
      padding: 0.85rem;
      border: 1px solid rgba(255, 255, 255, 0.16);
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.1);
    }

    span,
    strong {
      display: block;
    }

    span {
      color: rgba(255, 255, 255, 0.68);
      font-size: 0.78rem;
    }

    strong {
      color: #fff;
      font-size: 1.1rem;
    }
  }

  .settings-sidebar {
    position: sticky;
    top: 5.75rem;
    display: grid;
    gap: 0.65rem;
  }

  .settings-search {
    margin-bottom: 0.35rem;

    .input-group-text,
    .form-control {
      border-color: #e7e7ef;
      background: #fff;
    }

    .form-control {
      min-height: 42px;
    }
  }

  .settings-category {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0.75rem 0.85rem;
    color: #5e5873;
    border: 1px solid #ebe9f1;
    border-radius: 8px;
    background: #fff;
    text-align: left;
    transition: all 0.18s ease;

    span {
      display: inline-flex;
      align-items: center;
      gap: 0.55rem;
      font-weight: 600;
    }

    svg {
      width: 16px;
      height: 16px;
    }

    .badge {
      color: #5e5873;
      background: #f3f2f7;
    }

    &.active,
    &:hover {
      color: #fff;
      border-color: #2b8a7e;
      background: #2b8a7e;
      box-shadow: 0 10px 22px rgba(43, 138, 126, 0.22);

      .badge {
        color: #2b8a7e;
        background: #fff;
      }
    }
  }

  .settings-workspace {
    border: 0;
    border-radius: 8px;
    box-shadow: 0 10px 30px rgba(34, 41, 47, 0.08);
  }

  .settings-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.25rem 1.25rem 1rem;
    border-bottom: 1px solid #ebe9f1;

    h2 {
      margin-bottom: 0.1rem;
      font-size: 1.25rem;
      font-weight: 700;
      letter-spacing: 0;
    }

    p {
      margin-bottom: 0;
      color: #6e6b7b;
    }
  }

  .settings-actions {
    display: flex;
    gap: 0.75rem;
  }

  .settings-section {
    padding: 1.25rem;
    border-bottom: 1px solid #f0eef6;

    &:last-child {
      border-bottom: 0;
    }
  }

  .settings-section__header {
    display: flex;
    gap: 0.85rem;
    margin-bottom: 1rem;

    h3 {
      margin-bottom: 0.15rem;
      font-size: 1.05rem;
      font-weight: 700;
      letter-spacing: 0;
    }

    p {
      margin-bottom: 0;
      color: #6e6b7b;
    }
  }

  .settings-section__icon {
    display: grid;
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    place-items: center;
    color: #2b8a7e;
    border-radius: 8px;
    background: rgba(43, 138, 126, 0.11);
  }

  .settings-field {
    margin-bottom: 1.2rem;

    .form-control {
      min-height: 42px;
      border-color: #dedbe8;
      border-radius: 8px;
    }
  }

  .settings-field__label {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.45rem;

    label {
      margin-bottom: 0;
      color: #3f3b57;
      font-weight: 700;
    }

    small {
      color: #9a96aa;
      text-align: right;
      word-break: break-word;
    }
  }

  .settings-loading,
  .settings-empty {
    display: grid;
    min-height: 260px;
    place-items: center;
    color: #6e6b7b;
  }

  .settings-empty {
    gap: 0.35rem;
    padding: 3rem 1rem;

    strong,
    span {
      display: block;
    }
  }
}

@media (max-width: 991.98px) {
  .settings-page {
    .settings-header,
    .settings-toolbar {
      flex-direction: column;
      align-items: stretch;
    }

    .settings-header__meta {
      min-width: 0;
    }

    .settings-sidebar {
      position: static;
    }
  }
}
</style>
